import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, Plus, ServerCrash, CheckCircle2, Clock, RefreshCw, Users, Database, PauseCircle, PlayCircle, XCircle, ExternalLink } from "lucide-react";

// ── Tipos ─────────────────────────────────────────────────────────────────

interface TenantResumo {
  slug: string;
  tenantId: string;
  dbName: string;
  status: string;
  createdAt: string;
  lifecycleStatus: string;
  trialExpiraEm?: string | null;
  canceladoEm?: string | null;
  /** Produtos web contratados ("gestor", "chef", "shop"). Vazio + definido = nenhum (só Desktop). */
  produtos?: string[];
  /** false = nunca definido: o login entra no Gestor e no Chef (não no Shop). */
  produtosDefinidos?: boolean;
}

type Produto = "gestor" | "chef" | "shop";
const PRODUTOS: { id: Produto; nome: string }[] = [
  { id: "gestor", nome: "Gestor" },
  { id: "chef", nome: "Chef" },
  { id: "shop", nome: "Shop" },
];

/** Produtos em que o login da loja entra (nunca definido = Gestor e Chef; o Shop precisa ser ligado). */
function produtosEfetivos(t: TenantResumo): Produto[] {
  if (!t.produtosDefinidos) return ["gestor", "chef"];
  return (t.produtos ?? []).filter((x): x is Produto => PRODUTOS.some(p => p.id === x));
}

function mensagemErro(err: unknown, padrao: string): string {
  return (err as { response?: { data?: { erro?: string } } })?.response?.data?.erro ?? padrao;
}

// ── Hooks ─────────────────────────────────────────────────────────────────

function useTenants() {
  return useQuery<TenantResumo[]>({
    queryKey: ["admin", "tenants"],
    queryFn: async () => {
      const { data } = await apiClient.get("/api/admin/tenants");
      return data;
    },
  });
}

// ── Utilitários ────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  if (status === "PRONTO")
    return <Badge className="bg-green-100 text-green-800 border-green-200"><CheckCircle2 className="h-3 w-3 mr-1" />Pronto</Badge>;
  if (status === "PROVISIONING" || status === "RUNNING")
    return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200"><Clock className="h-3 w-3 mr-1" />Provisionando</Badge>;
  return <Badge variant="destructive"><ServerCrash className="h-3 w-3 mr-1" />{status}</Badge>;
}

function LifecycleBadge({ tenant }: { tenant: TenantResumo }) {
  if (tenant.lifecycleStatus === "SUSPENSO")
    return <Badge className="bg-orange-100 text-orange-800 border-orange-200"><PauseCircle className="h-3 w-3 mr-1" />Suspenso</Badge>;
  if (tenant.lifecycleStatus === "CANCELADO")
    return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Cancelado</Badge>;
  if (tenant.trialExpiraEm) {
    const expirado = new Date(tenant.trialExpiraEm) <= new Date();
    return expirado
      ? <Badge className="bg-red-100 text-red-800 border-red-200">Trial expirado</Badge>
      : <Badge className="bg-blue-100 text-blue-800 border-blue-200">Trial até {new Date(tenant.trialExpiraEm).toLocaleDateString("pt-BR")}</Badge>;
  }
  return null;   // ATIVO pago = sem badge (estado normal)
}

// ── Página principal ───────────────────────────────────────────────────────

export default function GestorHub() {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!user?.isAdminGlobal) {
    navigate("/");
    return null;
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Building2 className="h-6 w-6" /> Gestor Hub — Suporte Udisys
        </h1>
        <p className="text-muted-foreground">Gerenciamento de clientes e provisionamento de ambientes.</p>
      </div>

      <Tabs defaultValue="clientes">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="clientes">Clientes ativos</TabsTrigger>
          <TabsTrigger value="novo">Novo cliente Delivery</TabsTrigger>
          <TabsTrigger value="migrar">Registrar migração Horus</TabsTrigger>
        </TabsList>

        <TabsContent value="clientes" className="mt-4">
          <ClientesTab />
        </TabsContent>

        <TabsContent value="novo" className="mt-4">
          <NovoClienteTab />
        </TabsContent>

        <TabsContent value="migrar" className="mt-4">
          <MigrarClienteTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Aba: Clientes ─────────────────────────────────────────────────────────

function ClientesTab() {
  const { data: todosTenants, isLoading, refetch } = useTenants();
  const qc = useQueryClient();

  const [busca, setBusca] = useState("");
  const [situacao, setSituacao] = useState<"todos" | "ATIVO" | "TRIAL" | "SUSPENSO" | "CANCELADO">("todos");
  const termo = busca.trim().toLowerCase();
  const tenants = (todosTenants ?? []).filter(t => {
    if (termo && ![t.slug, t.dbName, t.tenantId].some(v => v?.toLowerCase().includes(termo))) return false;
    if (situacao === "todos") return true;
    if (situacao === "TRIAL") return t.lifecycleStatus === "ATIVO" && !!t.trialExpiraEm;
    if (situacao === "ATIVO") return t.lifecycleStatus === "ATIVO" && !t.trialExpiraEm;
    return t.lifecycleStatus === situacao;
  });

  // Ações de ciclo de vida (suspender/reativar/cancelar) por tenant.
  const lifecycle = useMutation({
    mutationFn: async ({ slug, acao }: { slug: string; acao: "suspender" | "reativar" | "cancelar" }) => {
      const body = acao === "reativar" ? { encerrarTrial: false } : undefined;
      const { data } = await apiClient.post(`/api/admin/tenants/${slug}/${acao}`, body);
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Tenant "${data.slug}" → ${data.lifecycle}`);
      qc.invalidateQueries({ queryKey: ["admin", "tenants"] });
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { erro?: string } } })?.response?.data?.erro ?? "Erro na ação.";
      toast.error(msg);
    },
  });

  // Produtos contratados: decidem em qual app (Gestor Web / painel do Chef) o login entra.
  const produtos = useMutation({
    mutationFn: async ({ slug, lista }: { slug: string; lista: Produto[] }) => {
      const { data } = await apiClient.put(`/api/admin/tenants/${slug}/produtos`, { produtos: lista });
      return data as { slug: string; produtos: string[] };
    },
    onSuccess: (data) => {
      toast.success(`Produtos de "${data.slug}": ${data.produtos.join(" + ") || "nenhum (só Desktop)"}`);
      qc.invalidateQueries({ queryKey: ["admin", "tenants"] });
    },
    onError: (err: unknown) => toast.error(mensagemErro(err, "Erro ao salvar os produtos.")),
  });

  const alternarProduto = (t: TenantResumo, produto: Produto) => {
    const atuais = produtosEfetivos(t);
    const lista = atuais.includes(produto) ? atuais.filter(p => p !== produto) : [...atuais, produto];
    const nome = PRODUTOS.find(p => p.id === produto)!.nome;
    const tirando = atuais.includes(produto);
    const aviso = lista.length
      ? "Quem estiver logado nele perde o acesso em até 30 segundos."
      : "A loja fica SEM produto web (só o Gestor Desktop): ninguém dela entra no Gestor Web, no painel do Chef nem no Shop.";
    if (tirando && !window.confirm(`Tirar o Udisys ${nome} de "${t.slug}"?\n\n${aviso}`)) return;
    produtos.mutate({ slug: t.slug, lista });
  };

  // "Acessar painel": abre o painel do Chef da loja já logado como suporte. A aba
  // abre ANTES do await (senão o navegador bloqueia como pop-up) e só depois recebe a URL.
  const acessarPainel = async (slug: string) => {
    const aba = window.open("about:blank", "_blank");
    try {
      const { data } = await apiClient.post<{ url: string }>(`/api/admin/tenants/${slug}/acesso-suporte`);
      if (aba) {
        aba.opener = null;
        aba.location.href = data.url;
      } else {
        window.open(data.url, "_blank", "noopener");
      }
    } catch (err) {
      aba?.close();
      toast.error(mensagemErro(err, "Não foi possível gerar o acesso ao painel."));
    }
  };

  const confirmarCancelar = (slug: string) => {
    if (window.confirm(
      `Cancelar o cliente "${slug}"?\n\nO login será BARRADO imediatamente. ` +
      `O banco fica preservado pela janela de retenção (drop é outra ação, manual).`,
    )) lifecycle.mutate({ slug, acao: "cancelar" });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-full sm:w-72">
          <Label className="text-xs text-muted-foreground">Busca</Label>
          <Input placeholder="Slug, banco ou tenant id..." value={busca} onChange={e => setBusca(e.target.value)} />
        </div>
        <div className="w-44">
          <Label className="text-xs text-muted-foreground">Situação</Label>
          <Select value={situacao} onValueChange={v => setSituacao(v as typeof situacao)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todas</SelectItem>
              <SelectItem value="ATIVO">Ativo (pago)</SelectItem>
              <SelectItem value="TRIAL">Em trial</SelectItem>
              <SelectItem value="SUSPENSO">Suspenso</SelectItem>
              <SelectItem value="CANCELADO">Cancelado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <span className="text-sm text-muted-foreground pb-2">
          {tenants.length} de {todosTenants?.length ?? 0}
        </span>
        <Button variant="outline" size="sm" className="ml-auto" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" /> Atualizar
        </Button>
      </div>

      {isLoading ? (
        <p className="text-center text-muted-foreground py-8">Carregando...</p>
      ) : !todosTenants?.length ? (
        <p className="text-center text-muted-foreground py-8">Nenhum cliente cadastrado.</p>
      ) : !tenants.length ? (
        <p className="text-center text-muted-foreground py-8">Nenhum cliente com estes filtros.</p>
      ) : (
        <div className="grid gap-3">
          {tenants.map(t => (
            <Card key={t.slug} className="border">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-base">{t.slug}</span>
                      <StatusBadge status={t.status} />
                      <LifecycleBadge tenant={t} />
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1"><Database className="h-3 w-3" />{t.dbName}</span>
                      <span className="font-mono text-xs truncate max-w-xs">{t.tenantId}</span>
                    </div>
                    <div className="flex items-center gap-1 pt-1">
                      <span className="text-xs text-muted-foreground mr-1">Produtos:</span>
                      {PRODUTOS.map(p => {
                        const ativo = produtosEfetivos(t).includes(p.id);
                        return (
                          <Button
                            key={p.id}
                            type="button"
                            size="sm"
                            variant={ativo ? "default" : "outline"}
                            className="h-6 px-2 text-xs"
                            onClick={() => alternarProduto(t, p.id)}
                            disabled={produtos.isPending}
                            title={ativo ? `Tirar o Udisys ${p.nome}` : `Liberar o Udisys ${p.nome}`}
                          >
                            {p.nome}
                          </Button>
                        );
                      })}
                      {!t.produtosDefinidos ? (
                        <span className="text-xs text-muted-foreground ml-1">(não definido: entra no Gestor e no Chef)</span>
                      ) : !t.produtos?.length && (
                        <span className="text-xs text-muted-foreground ml-1">(nenhum produto web: só Desktop)</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Criado em {new Date(t.createdAt).toLocaleDateString("pt-BR")}
                      {t.canceladoEm && ` · Cancelado em ${new Date(t.canceladoEm).toLocaleDateString("pt-BR")}`}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {t.status === "PRONTO" && t.lifecycleStatus !== "CANCELADO" && produtosEfetivos(t).includes("chef") && (
                      <Button
                        variant="outline" size="sm"
                        onClick={() => acessarPainel(t.slug)}
                        title="Abre o painel do Chef da loja já logado como suporte (sessão de 2h)"
                      >
                        <ExternalLink className="h-4 w-4 mr-1" /> Acessar painel
                      </Button>
                    )}
                    {t.lifecycleStatus === "ATIVO" ? (
                      <Button
                        variant="outline" size="sm"
                        onClick={() => lifecycle.mutate({ slug: t.slug, acao: "suspender" })}
                        disabled={lifecycle.isPending}
                        title="Barra o login até reativar (inadimplência/pausa)"
                      >
                        <PauseCircle className="h-4 w-4 mr-1" /> Suspender
                      </Button>
                    ) : (
                      <Button
                        variant="outline" size="sm"
                        onClick={() => lifecycle.mutate({ slug: t.slug, acao: "reativar" })}
                        disabled={lifecycle.isPending}
                        title="Libera o login novamente"
                      >
                        <PlayCircle className="h-4 w-4 mr-1" /> Reativar
                      </Button>
                    )}
                    {t.lifecycleStatus !== "CANCELADO" && (
                      <Button
                        variant="outline" size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => confirmarCancelar(t.slug)}
                        disabled={lifecycle.isPending}
                      >
                        <XCircle className="h-4 w-4 mr-1" /> Cancelar
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Aba: Novo cliente (onboarding em 1 clique; Gestor / Delivery / Ambos) ──

interface OnboardResultado {
  slug: string;
  painelUrl: string;
  conviteEnviado: boolean;
  /** Provisionou em background (cliente novo) — o convite sai no fim, sem resposta HTTP. */
  emBackground?: boolean;
}

/**
 * Espera o banco de um cliente NOVO ficar pronto. Provisionar leva minutos (cria o
 * banco e aplica todas as migrations), então o servidor responde 202 e segue em
 * background: quem diz que terminou é a lista de tenants, onde o slug aparece PRONTO.
 * Falha de rede no meio não interrompe — o provisionamento continua no servidor.
 */
async function aguardarPronto(slug: string, onTick: (tentativa: number) => void): Promise<boolean> {
  const limite = Date.now() + 20 * 60_000;
  let tentativa = 0;
  while (Date.now() < limite) {
    await new Promise(r => setTimeout(r, 10_000));
    onTick(++tentativa);
    try {
      const { data } = await apiClient.get<TenantResumo[]>("/api/admin/tenants");
      if (data.some(t => t.slug === slug && t.status === "PRONTO")) return true;
    } catch {
      /* instabilidade de rede: o provisionamento segue no servidor, continua pollando */
    }
  }
  return false;
}

function NovoClienteTab() {
  const qc = useQueryClient();
  const [slug, setSlug] = useState("");
  const [emailDono, setEmailDono] = useState("");
  const [nomeFantasia, setNomeFantasia] = useState("");
  const [razaoSocial, setRazaoSocial] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [tipo, setTipo] = useState<"gestor" | "delivery" | "ambos">("delivery");
  const [resultado, setResultado] = useState<OnboardResultado | null>(null);
  const [espera, setEspera] = useState(0);

  const mutation = useMutation({
    mutationFn: async () => {
      setEspera(0);
      // Dispara o onboarding: provisiona banco + grava empresa + convida o admin.
      const { data } = await apiClient.post("/api/admin/tenants/onboard", {
        slug,
        emailDono,
        tipo,
        nomeFantasia: nomeFantasia || undefined,
        razaoSocial: razaoSocial || undefined,
        cnpj: cnpj || undefined,
      });
      const res = data as OnboardResultado & { tenantId?: string; status: string };

      // Banco já existia: veio tudo pronto na resposta.
      if (res.status !== "PROVISIONANDO") return { ...res, emBackground: false };

      // Cliente novo: o servidor só confirmou que COMEÇOU. Acompanha até PRONTO.
      const pronto = await aguardarPronto(res.slug, setEspera);
      if (!pronto)
        throw new Error(
          `O provisionamento de "${res.slug}" passou de 20 minutos sem ficar pronto. ` +
          `Confira a aba "Clientes ativos" e o log da API — ele pode ter terminado depois.`);
      return { ...res, emBackground: true };
    },
    onSuccess: (data) => {
      setResultado({
        slug: data.slug,
        painelUrl: data.painelUrl,
        conviteEnviado: data.conviteEnviado,
        emBackground: data.emBackground,
      });
      toast.success(`Cliente "${data.slug}" provisionado!`);
      qc.invalidateQueries({ queryKey: ["admin", "tenants"] });
      setSlug(""); setEmailDono(""); setNomeFantasia(""); setRazaoSocial(""); setCnpj("");
    },
    onError: (err: unknown) => {
      // Sem `response` (timeout do axios, rede caída) a mensagem do erro é a única
      // pista que existe — não engolir num texto genérico.
      const e = err as { response?: { data?: { erro?: string } }; message?: string };
      toast.error(e?.response?.data?.erro ?? e?.message ?? "Erro no onboarding.");
    },
  });

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailDono.trim());
  const valido = slug.trim().length >= 3 && emailOk;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><Plus className="h-4 w-4" /> Novo cliente (1 clique)</CardTitle>
        <CardDescription>
          Provisiona o banco <code>udisys_cli_{"<slug>"}</code>, grava a empresa e envia um
          <strong> convite por e-mail</strong> ao dono para ele definir a própria senha —
          ninguém digita a senha do cliente.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <Label>Tipo de cliente</Label>
          <div className="flex gap-2">
            {(["gestor", "delivery", "ambos"] as const).map(t => (
              <Button
                key={t}
                type="button"
                size="sm"
                variant={tipo === t ? "default" : "outline"}
                onClick={() => setTipo(t)}
              >
                {t === "gestor" ? "Gestor (ERP)" : t === "delivery" ? "Delivery" : "Ambos"}
              </Button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            {tipo === "gestor"
              ? "Convite e painel em app.udisysgestor.com (ERP). O login entra só no Gestor."
              : `Loja em ${slug || "<slug>"}.udisyschef.com; convite no domínio da loja. ` +
                (tipo === "ambos" ? "O login entra no Gestor e no Chef." : "O login entra só no Chef.")}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>Subdomínio (slug) <span className="text-destructive">*</span></Label>
            <Input
              placeholder="ex: padariasplanalto"
              value={slug}
              onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ""))}
            />
            <p className="text-xs text-muted-foreground">
              Identificador do cliente (banco <code>udisys_cli_{"<slug>"}</code>).
            </p>
          </div>
          <div className="space-y-1">
            <Label>E-mail do dono <span className="text-destructive">*</span></Label>
            <Input type="email" placeholder="dono@empresa.com.br" value={emailDono} onChange={e => setEmailDono(e.target.value)} />
            <p className="text-xs text-muted-foreground">Vira o login e recebe o convite p/ definir a senha.</p>
          </div>
        </div>

        <Separator />
        <p className="text-sm font-medium flex items-center gap-1"><Building2 className="h-4 w-4" /> Dados da empresa</p>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>Nome fantasia</Label>
            <Input placeholder="ex: Padaria Planalto" value={nomeFantasia} onChange={e => setNomeFantasia(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Razão social</Label>
            <Input value={razaoSocial} onChange={e => setRazaoSocial(e.target.value)} />
          </div>
        </div>
        <div className="space-y-1">
          <Label>CNPJ</Label>
          <Input placeholder="00.000.000/0000-00" value={cnpj} onChange={e => setCnpj(e.target.value)} className="max-w-xs" />
        </div>

        {resultado && (
          <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800 space-y-1">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 shrink-0" /> Cliente <strong>{resultado.slug}</strong> provisionado.
            </div>
            <div>
              Painel: <a href={resultado.painelUrl} target="_blank" rel="noreferrer" className="underline font-medium">{resultado.painelUrl}</a>
            </div>
            <div>
              {resultado.emBackground
                ? "Convite enviado ao dono logo após o provisionamento — se não chegar, reenvie pelo fluxo \"esqueci minha senha\" (mesmo token)."
                : resultado.conviteEnviado
                  ? "Convite enviado ao dono ✅ (ele define a senha pelo link do e-mail)."
                  : "⚠️ Convite NÃO enviado — reenvie pelo fluxo \"esqueci minha senha\" ou verifique o SMTP."}
            </div>
          </div>
        )}

        {mutation.isPending && espera > 0 && (
          <div className="rounded-md bg-muted p-3 text-sm flex items-center gap-2">
            <Clock className="h-4 w-4 shrink-0 animate-pulse" />
            Criando o banco e aplicando as migrations — leva alguns minutos (~{espera * 10}s).
            Pode deixar esta aba aberta; o provisionamento segue no servidor mesmo se você sair.
          </div>
        )}

        <Button onClick={() => mutation.mutate()} disabled={!valido || mutation.isPending} className="w-full">
          {mutation.isPending ? "Provisionando..." : "Provisionar e convidar"}
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Aba: Registrar migração Horus ─────────────────────────────────────────

function MigrarClienteTab() {
  const qc = useQueryClient();
  const [slug, setSlug] = useState("infinitamente");
  const [dbName, setDbName] = useState("udisys_52630410000110");
  const [tenantId, setTenantId] = useState("");
  const [login, setLogin] = useState("wigor@infinitamentesucesso.com.br");
  const [senha, setSenha] = useState("");
  const [nome, setNome] = useState("Wigor Cadore");
  const [etapa, setEtapa] = useState<"idle" | "registrando" | "semeando" | "ok" | "erro">("idle");
  const [erro, setErro] = useState("");

  const executar = async () => {
    if (!slug || !dbName || !login || !senha) return;
    setEtapa("registrando");
    setErro("");
    try {
      // 1. Registrar banco existente no control plane
      await apiClient.post("/api/admin/tenants/register-existing", {
        slug,
        databaseName: dbName,
        tenantId: tenantId || undefined,
      });

      // 2. Criar usuário admin
      setEtapa("semeando");
      await apiClient.post("/api/admin/tenants/seed-admin", {
        slug, login, senha, nome: nome || undefined, email: login, adminGlobal: false,
      });

      setEtapa("ok");
      toast.success(`Cliente "${slug}" registrado! Login por e-mail em app.udisysgestor.com`);
      qc.invalidateQueries({ queryKey: ["admin", "tenants"] });
    } catch (err) {
      const msg = (err as { response?: { data?: { erro?: string } } })?.response?.data?.erro ?? "Erro ao registrar.";
      setErro(msg);
      setEtapa("erro");
      toast.error(msg);
    }
  };

  const valido = slug.trim().length >= 3 && dbName.trim().length >= 3 && login.trim().length >= 3 && senha.length >= 6;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><Database className="h-4 w-4" /> Registrar banco já importado (migração Horus)</CardTitle>
        <CardDescription>
          Para bancos que já existem na VPS (importados via importer Horus→PG).
          Registra no control plane e cria o admin, <strong>sem criar banco novo</strong>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>Slug <span className="text-destructive">*</span></Label>
            <Input
              value={slug}
              onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ""))}
              placeholder="ex: infinitamente"
            />
            <p className="text-xs text-muted-foreground">Identificador interno do cliente (acesso é por e-mail em <strong>app.udisysgestor.com</strong>)</p>
          </div>
          <div className="space-y-1">
            <Label>Nome do banco na VPS <span className="text-destructive">*</span></Label>
            <Input
              value={dbName}
              onChange={e => setDbName(e.target.value)}
              placeholder="ex: udisys_52630410000110"
            />
          </div>
        </div>

        <div className="space-y-1">
          <Label>Tenant ID (deixe em branco para gerar automaticamente)</Label>
          <Input
            value={tenantId}
            onChange={e => setTenantId(e.target.value)}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            className="font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Se já importou dados com <code>--tenant &lt;guid&gt;</code>, cole o mesmo GUID aqui.
          </p>
        </div>

        <Separator />
        <p className="text-sm font-medium flex items-center gap-1"><Users className="h-4 w-4" /> Admin inicial do cliente</p>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>Nome (opcional)</Label>
            <Input value={nome} onChange={e => setNome(e.target.value)} placeholder="ex: Wigor Cadore" />
          </div>
          <div className="space-y-1">
            <Label>E-mail / login <span className="text-destructive">*</span></Label>
            <Input type="email" value={login} onChange={e => setLogin(e.target.value)} />
          </div>
        </div>
        <div className="space-y-1">
          <Label>Senha inicial <span className="text-destructive">*</span></Label>
          <Input type="password" value={senha} onChange={e => setSenha(e.target.value)} placeholder="Mínimo 6 caracteres" />
        </div>

        {etapa === "ok" && (
          <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            Cliente registrado. Login: <strong>{login}</strong> em <strong>app.udisysgestor.com</strong>
          </div>
        )}
        {etapa === "erro" && (
          <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3 text-sm text-destructive">
            {erro}
          </div>
        )}

        <Button onClick={executar} disabled={!valido || etapa === "registrando" || etapa === "semeando"} className="w-full">
          {etapa === "registrando" && "Registrando no control plane..."}
          {etapa === "semeando" && "Criando usuário admin..."}
          {(etapa === "idle" || etapa === "erro" || etapa === "ok") && "Registrar cliente"}
        </Button>
      </CardContent>
    </Card>
  );
}
